import type {ComponentInternalInstance} from '@vue/composition-api'

import ApiGatewayClient from '@/api/aws/aws-gateway-client'
import type {MFContext} from '@/types/microfrontend'
import {parseDealerSchedule} from '@/utils/dealer-schedule'

function createStableCallbacks(instance: ComponentInternalInstance) {
  const store = instance.proxy.$store
  const router = instance.proxy.$router

  return {
    navigate: (path: string) => router?.push(path),
    navigateToLead: (leadId: string) => router?.push(`/leads/${leadId}`),
    fetchWithAuth: async (path: string, options?: any) => {
      const apiGatewayClient = new ApiGatewayClient()
      try {
        const api = options?.api || 'rido'
        const endpointMap: Record<string, string> = {
          rido: process.env.VUE_APP_MAPIT_RIDO_API || '',
          contacts: process.env.VUE_APP_CONTACTS_API || '',
          'honda-catalog': process.env.VUE_APP_HONDA_CATALOG_API || 'https://honda-catalog.dev.mapit.me',
        }
        const endpoint = endpointMap[api]

        const res = await apiGatewayClient.sendRequest({
          endpoint,
          method: (options?.method || 'GET') as any,
          path,
          queryParams: options?.queryParams,
          data: options?.body,
        })

        return {
          ok: res.status >= 200 && res.status < 300,
          status: res.status,
          json: async () => res.data,
        }
      } catch (err: any) {
        return {
          ok: false,
          status: err.response?.status || 500,
          json: async () => err.response?.data || {error: err.message},
        }
      }
    },
    showSnackbar: (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
      store?.dispatch('setAlertResult', {
        stringId: message,
        type,
        open: true,
      })
    },
  }
}

const callbackCache = new WeakMap<ComponentInternalInstance, ReturnType<typeof createStableCallbacks>>()

export function buildMFContext(instance: ComponentInternalInstance | null, basePath: string): MFContext {
  if (!instance?.proxy) {
    throw new Error('buildMFContext requires a valid Vue instance')
  }

  const store = instance.proxy.$store
  const route = instance.proxy.$route
  const i18n = instance.proxy.$i18n

  // Get or create stable callbacks for this instance
  let callbacks = callbackCache.get(instance)
  if (!callbacks) {
    callbacks = createStableCallbacks(instance)
    callbackCache.set(instance, callbacks)
  }

  // Getters from Vuex store
  const dealerId = store?.getters.getDealerId || ''
  const user = store?.getters.getUser
  const cognitoUser = store?.getters.getCognitoUser
  const authAttributes = cognitoUser?.attributes || {}
  const normalizeName = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
  const authNameFromToken = normalizeName(authAttributes.displayName || authAttributes.name)
  const authFullNameFromToken = normalizeName(
    [authAttributes.given_name, authAttributes.family_name].filter(Boolean).join(' '),
  )
  const cognitoUserNameRaw = store?.getters.getCognitoUserName || ''
  const cognitoUserName = typeof cognitoUserNameRaw === 'string' ? cognitoUserNameRaw.trim() : ''
  const authDisplayName = authNameFromToken || authFullNameFromToken || cognitoUserName
  const hasValidAuthDisplayName =
    authDisplayName.length > 0 && !/^(undefined|null)(?:\s+(undefined|null))*$/i.test(authDisplayName)
  const userEmail = user?.email || user?.login || ''
  const userProvidedName = normalizeName(user?.name)
  const userName =
    (userProvidedName && userProvidedName !== userEmail ? userProvidedName : '') ||
    (hasValidAuthDisplayName ? authDisplayName : '') ||
    userProvidedName ||
    userEmail
  const userId = user?.id || userEmail
  const permissions = store?.getters.getPermissions
  const locale = i18n?.locale || 'es'
  const country = store?.getters.getCountry || 'ES'
  const group = store?.getters.getGroup
  const salesHours = group?.profile?.salesAppointmentConditions?.openingHours
  const shopHours = group?.profile?.shop?.openingHours
  const openingHours = salesHours?.length ? salesHours : shopHours
  const dealerSchedule = parseDealerSchedule(openingHours)

  return {
    dealerId,
    user: {
      id: userId,
      name: userName,
      email: userEmail,
    },
    permissions: {
      salesAppointments: {
        canSee: permissions?.salesAppointments?.canSee ?? false,
        canCreate: permissions?.salesAppointments?.canCreate ?? false,
        canEdit: permissions?.salesAppointments?.canEdit ?? false,
        canDelete: permissions?.salesAppointments?.canDelete ?? false,
      },
      leads: {
        canSee: permissions?.leads?.canSee ?? false,
      },
    },
    locale: locale as 'es' | 'pt',
    country,
    basePath,
    initialPath: route?.path || basePath,
    dealerSchedule,
    navigate: callbacks.navigate,
    navigateToLead: callbacks.navigateToLead,
    fetchWithAuth: callbacks.fetchWithAuth,
    showSnackbar: callbacks.showSnackbar,
  }
}
